// src/App.js
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from 'react-router-dom';

import InboxPage from './pages/InboxPage';
import CalendarPage from './pages/CalendarPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AccountingPage from './pages/AccountingPage';
import UsersPage from './pages/UsersPage';
import CarsPage from './pages/CarsPage';
import MaintenancePage from './pages/MaintenancePage';
import ApprovalsPage from './pages/ApprovalsPage';
import AuditTrailPage from './pages/AuditTrailPage';
import LandingPage from './pages/LandingPage';
import PricesPage from './pages/PricesPage';
import PayReturnPage from './pages/PayReturnPage';
import PayCancelPage from './pages/PayCancelPage';
import PayStartPage from './pages/PayStartPage';
import PaymentOpsPage from './pages/PaymentOpsPage';

import { fetchAuthSession } from 'aws-amplify/auth';
import ConsoleAuthGate from './auth/ConsoleAuthGate';
import RequireGroup from './auth/RequireGroup';
import ConfirmModal from './components/ConfirmModal';
import { createAuditedClient } from './utils/auditClient';
import { CountryProvider, useCountry, COUNTRY_OPTIONS } from './context/CountryContext';
import HandoverFormPage from './pages/HandoverFormPage';
import HandoverMediaPage from './pages/HandoverMediaPage';

import {
  Inbox,
  Calendar,
  Car,
  Wrench,
  Users,
  CheckCircle2,
  History,
  ReceiptText,
  BarChart3,
  DollarSign,
  CreditCard,
} from 'lucide-react';
import logo from './logo.png';

/* -------------------- Layout helpers -------------------- */
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

const client = createAuditedClient('App');
export const InboxBadgeContext = React.createContext({
  inboxCount: 0,
  setInboxCount: () => { },
});

export function useInboxBadge() {
  return React.useContext(InboxBadgeContext);
}

const GetCompanyName = /* GraphQL */ `
  query GetCompany($id: ID!) {
    getCompany(id: $id) {
      id
      name
    }
  }
`;

const PendingCR = /* GraphQL */ `
  query PendingCR {
    listChangeRequests(limit: 1000, filter: { status: { eq: PENDING } }) {
      items {
        id
        CompanyId
        entity
        targetId
      }
      nextToken
    }
  }
`;

const PendingInboxEmails = /* GraphQL */ `
  query PendingInboxEmails($CompanyId: String!, $since: String!) {
    listEmailMessages(
      limit: 1000
      filter: {
        CompanyId: { eq: $CompanyId }
        isRead: { ne: true }
        receivedAt: { ge: $since }
      }
    ) {
      items {
        id
      }
      nextToken
    }
  }
`;

const PendingReservations = /* GraphQL */ `
  query PendingReservations {
    listReservations(limit: 1000, filter: { approvalStatus: { eq: PENDING } }) {
      items {
        id
        CompanyId
      }
      nextToken
    }
  }
`;

const PendingMaintenance = /* GraphQL */ ` 
  query PendingMaintenance {
    listMaintenances(limit: 1000, filter: { approvalStatus: { eq: PENDING } }) {
      items {
        id
        CompanyId
      }
      nextToken
    }
  }
`;

/** tiny shared hook to detect mobile-ish width */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);

  return isMobile;
}

/* -------------------- Country toggle pill -------------------- */

function CountryTogglePill() {
  const { country, setCountry } = useCountry();

  return (
    <div className="CountryPill">
      {COUNTRY_OPTIONS.map((opt) => {
        const active = country === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => setCountry(opt.value)}
            className={`CountryBtn ${active ? 'CountryBtnActive' : ''}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}


function NavStyles() {
  const css = `
  :root{
    --nav-bg: rgba(255,255,255,.78);
    --nav-solid: #ffffff;
    --nav-stroke: rgba(17,24,39,.10);
    --nav-shadow: 0 18px 45px rgba(17,24,39,.10), 0 2px 10px rgba(17,24,39,.06);
    --nav-text: #0f172a;
    --nav-sub: rgba(15,23,42,.62);
    --nav-lime: #83d466;
    --nav-lime2: #6bbf54;
    --nav-danger: #ef4444;
    --nav-radius: 18px;
    --nav-radius2: 14px;
    --nav-ease: cubic-bezier(.2,.9,.2,1);
  }

  /* container */
  .NavWrap{
    padding: 16px;
    position: relative;
  }

  /* brand */
  .NavBrand{
    display:flex;
    align-items:center;
    gap:12px;
    text-decoration:none;
    padding: 10px 10px;
    border-radius: 14px;
    transition: transform .25s var(--nav-ease), background .25s var(--nav-ease);
  }
  .NavBrand:hover{ background: rgba(131,212,102,.10); transform: translateY(-1px); }
  .NavBrand:active{ transform: translateY(0px) scale(.99); }

  .NavBrandTitle{ line-height: 1.05; }
  .NavBrandTitle .t1{ font-weight: 900; letter-spacing: -.02em; color: var(--nav-text); font-size: 14px; }
  .NavBrandTitle .t2{ font-size: 11px; color: var(--nav-sub); margin-top: 2px; }

  /* card background for sidebar (desktop) */
  .NavCard{
    background: var(--nav-bg);
    border: 1px solid var(--nav-stroke);
    border-radius: var(--nav-radius);
    box-shadow: var(--nav-shadow);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    overflow: hidden;
  }
  .NavInner{
    padding: 14px;
  }

  /* section spacing */
  .NavSection{ margin-top: 10px; }
  .NavLinks{ display:flex; flex-direction:column; gap: 6px; margin-top: 10px; }

  /* nav item */
  .NavItem{
    --a: 0;
    position: relative;
    display:flex;
    align-items:center;
    gap:10px;
    text-decoration:none;
    padding: 10px 12px;
    border-radius: 14px;
    color: rgba(15,23,42,.74);
    font-weight: 700;
    font-size: 14px;
    letter-spacing: -.01em;
    transition:
      transform .22s var(--nav-ease),
      background .22s var(--nav-ease),
      color .22s var(--nav-ease);
    user-select: none;
    isolation: isolate;
  }

  .NavItem::before{
    content:"";
    position:absolute;
    inset: 0;
    border-radius: 14px;
    background: radial-gradient(120px 60px at 20% 0%, rgba(131,212,102,.22), transparent 60%),
                linear-gradient(180deg, rgba(255,255,255,.55), rgba(255,255,255,.20));
    opacity: 0;
    transition: opacity .22s var(--nav-ease);
    z-index: -1;
  }

  .NavItem::after{
    content:"";
    position:absolute;
    left: 8px;
    top: 8px;
    bottom: 8px;
    width: 4px;
    border-radius: 99px;
    background: linear-gradient(180deg, var(--nav-lime), var(--nav-lime2));
    transform: scaleY(var(--a));
    transform-origin: center;
    opacity: var(--a);
    transition: transform .22s var(--nav-ease), opacity .22s var(--nav-ease);
  }

  .NavItem:hover{ background: rgba(15,23,42,.04); color: rgba(15,23,42,.92); transform: translateY(-1px); }
  .NavItem:hover::before{ opacity: .9; }
  .NavItem:active{ transform: translateY(0px) scale(.99); }

  .NavItemActive{
    --a: 1;
    color: #0b1220;
    background: rgba(131,212,102,.16);
  }
  .NavItemActive::before{ opacity: 1; }

  .NavIcon{
    width: 18px;
    height: 18px;
    opacity: .92;
  }

  /* badges */
  .NavBadge{
    margin-left: auto;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    border-radius: 999px;
    background: rgba(239,68,68,.95);
    color: #fff;
    font-size: 11px;
    line-height: 18px;
    font-weight: 900;
    letter-spacing: -.02em;
    box-shadow: 0 8px 18px rgba(239,68,68,.22);
  }

  /* user card */
  .NavUser{
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(17,24,39,.10);
  }
  .NavEmail{ font-size: 12px; color: var(--nav-text); font-weight: 800; }
  .NavCompany{ font-size: 11px; color: var(--nav-sub); margin-top: 3px; }

  .NavSignOut{
    margin-top: 10px;
    width: 100%;
    background: rgba(239,68,68,.10);
    color: #7f1d1d;
    border: 1px solid rgba(239,68,68,.25);
    padding: 10px 12px;
    border-radius: 14px;
    cursor: pointer;
    font-weight: 900;
    letter-spacing: -.01em;
    transition: transform .20s var(--nav-ease), background .20s var(--nav-ease), border-color .20s var(--nav-ease);
  }
  .NavSignOut:hover{ background: rgba(239,68,68,.14); border-color: rgba(239,68,68,.35); transform: translateY(-1px); }
  .NavSignOut:active{ transform: translateY(0px) scale(.99); }

  /* country pill upgrade (still inline-compatible) */
  .CountryPill{
    display:inline-flex;
    border-radius: 999px;
    border: 1px solid rgba(17,24,39,.12);
    overflow:hidden;
    background: rgba(255,255,255,.65);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow: 0 10px 24px rgba(17,24,39,.08);
  }
  .CountryBtn{
    padding: 7px 10px;
    border: none;
    cursor: pointer;
    font-weight: 900;
    font-size: 11px;
    color: rgba(15,23,42,.70);
    background: transparent;
    transition: background .18s var(--nav-ease), color .18s var(--nav-ease), transform .18s var(--nav-ease);
  }
  .CountryBtn:hover{ transform: translateY(-1px); }
  .CountryBtnActive{
    background: linear-gradient(180deg, rgba(17,24,39,.92), rgba(17,24,39,.80));
    color: #fff;
  }

  /* mobile top bar */
  .TopBar{
    position: sticky;
    top: 0;
    z-index: 50;
    padding: 10px 12px;
    background: rgba(255,255,255,.78);
    border-bottom: 1px solid rgba(17,24,39,.10);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  .TopRow{
    max-width: 1300px;
    margin: 0 auto;
    display:flex;
    align-items:center;
    gap: 10px;
  }
  .Burger{
    margin-left: auto;
    border: 1px solid rgba(17,24,39,.14);
    background: rgba(255,255,255,.65);
    border-radius: 14px;
    padding: 10px 12px;
    cursor: pointer;
    transition: transform .2s var(--nav-ease), background .2s var(--nav-ease);
    font-weight: 900;
    line-height: 1;
  }
  .Burger:hover{ transform: translateY(-1px); background: rgba(131,212,102,.10); }
  .Burger:active{ transform: translateY(0px) scale(.99); }

  /* drawer */
  .DrawerOverlay{
    position: fixed;
    inset: 0;
    background: rgba(2,6,23,.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    opacity: 0;
    pointer-events: none;
    transition: opacity .25s var(--nav-ease);
    z-index: 9998;
  }
  .DrawerOverlayOpen{
    opacity: 1;
    pointer-events: auto;
  }

  .Drawer{
    position: fixed;
    top: 0;
    left: 0;
    width: min(90vw, 360px);
    height: 100vh;
    z-index: 9999;
    transform: translateX(-102%);
    transition: transform .30s var(--nav-ease);
    padding: 14px;
  }
  .DrawerOpen{ transform: translateX(0%); }

  .DrawerCard{
    height: 100%;
    background: rgba(255,255,255,1);
    border: 1px solid rgba(17,24,39,.10);
    border-radius: 20px;
    box-shadow: 0 30px 80px rgba(2,6,23,.28);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    overflow: hidden;
  }

  .DrawerHeader{
    display:flex;
    align-items:center;
    justify-content: space-between;
    padding: 12px 12px 10px 12px;
    border-bottom: 1px solid rgba(17,24,39,.10);
  }
  .DrawerClose{
    border: 1px solid rgba(17,24,39,.14);
    background: rgba(255,255,255,.60);
    border-radius: 14px;
    padding: 8px 10px;
    cursor: pointer;
    font-weight: 900;
    transition: transform .2s var(--nav-ease), background .2s var(--nav-ease);
  }
  .DrawerClose:hover{ transform: translateY(-1px); background: rgba(239,68,68,.10); }
  .DrawerClose:active{ transform: translateY(0px) scale(.99); }

  /* reduce motion */
  @media (prefers-reduced-motion: reduce){
    .NavItem, .NavBrand, .Burger, .Drawer, .DrawerOverlay, .CountryBtn, .NavSignOut{
      transition: none !important;
    }
  }
      /* ===== Mobile fit & ergonomics ===== */

  /* Make top row resilient */
  .TopRow{
    flex-wrap: nowrap;
    min-width: 0;
  }

  /* Let the country pill shrink without breaking layout */
  .TopRow .CountryPill{
    max-width: 52vw;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
  .TopRow .CountryPill::-webkit-scrollbar{ display:none; }

  /* Smaller burger hit area on tiny screens but still tappable */
  @media (max-width: 420px){
    .TopRow{
      gap: 8px;
    }
    .Burger{
      padding: 9px 10px;
      border-radius: 12px;
    }

    /* On very small phones, don’t force the pill in the top bar.
       Keep it inside the drawer (you already render it there). */
    .TopRow .CountryPill{
      display: none;
    }
  }

  /* Safe-area padding for iOS notches */
  .TopBar{
    padding-top: calc(10px + env(safe-area-inset-top));
  }
  .Drawer{
    padding-top: calc(14px + env(safe-area-inset-top));
  }

  `;
  return <style>{css}</style>;
}

function Nav({ onRequestSignOut }) {
  const { pathname } = useLocation();
  const [groups, setGroups] = useState([]);
  const [claims, setClaims] = useState(null);
  const [approvalCount, setApprovalCount] = useState(0);
  const [companyName, setCompanyName] = useState(null);
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const { selectedCompanyId, loadingCompany } = useCountry();
  const isAdmin = groups.includes('admin');
  const { inboxCount, setInboxCount } = useInboxBadge();

  useEffect(() => {
    (async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const p = tokens?.idToken?.payload || {};
        setGroups(p['cognito:groups'] || []);
        setClaims({
          CompanyId: p['custom:CompanyId'] ?? p['custom:companyId'] ?? null,
          sub: p.sub || null,
          email: p.email || null,
        });
      } catch {
        setGroups([]);
        setClaims(null);
      }
    })();
  }, []);

  // close drawer on route change
  useEffect(() => {
    if (isMobile) setMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // ESC to close drawer
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);
  // lock body scroll when drawer is open
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);
  useEffect(() => {
    let cancelled = false;

    async function loadApprovals() {
      if (!isAdmin) {
        if (!cancelled) setApprovalCount(0);
        return;
      }

      const companyId = selectedCompanyId || claims?.CompanyId;
      if (!companyId) {
        if (!cancelled) setApprovalCount(0);
        return;
      }

      try {
        const [crRes, resRes, mtRes] = await Promise.all([
          client.graphql({ query: PendingCR, authMode: 'userPool' }),
          client.graphql({ query: PendingReservations, authMode: 'userPool' }),
          client.graphql({ query: PendingMaintenance, authMode: 'userPool' }),
        ]);

        const crItems = (crRes.data?.listChangeRequests?.items || []).filter(
          (x) => x && x.CompanyId === companyId
        );
        const resItems = (resRes.data?.listReservations?.items || []).filter(
          (x) => x && x.CompanyId === companyId
        );
        const mtItems = (mtRes.data?.listMaintenances?.items || []).filter(
          (x) => x && x.CompanyId === companyId
        );

        const crResTargets = new Set(
          crItems
            .filter((cr) => cr.entity === 'RESERVATION' && cr.targetId)
            .map((cr) => String(cr.targetId))
        );
        const crMtTargets = new Set(
          crItems
            .filter((cr) => cr.entity === 'MAINTENANCE' && cr.targetId)
            .map((cr) => String(cr.targetId))
        );

        const crCount = crItems.length;
        const resCount = resItems.filter((r) => !crResTargets.has(String(r.id))).length;
        const mtCount = mtItems.filter((m) => !crMtTargets.has(String(m.id))).length;

        if (!cancelled) setApprovalCount(crCount + resCount + mtCount);
      } catch (e) {
        console.error('Failed to load approval count', e);
        if (!cancelled) setApprovalCount(0);
      }
    }

    loadApprovals();
    return () => { cancelled = true; };
  }, [isAdmin, selectedCompanyId, claims?.CompanyId]);

  useEffect(() => {
    let cancelled = false;

    async function loadInboxCount() {
      if (loadingCompany) return;

      const canInbox = groups.includes('admin') || groups.includes('country_admin');
      if (!canInbox) {
        if (!cancelled) setInboxCount(0);
        return;
      }

      const companyId = selectedCompanyId || claims?.CompanyId || 'root';
      if (!companyId) return;

      try {
        const res = await client.graphql({
          query: PendingInboxEmails,
          variables: { CompanyId: companyId, since: isoDaysAgo(30) },
          authMode: 'userPool',
        });

        const items = res.data?.listEmailMessages?.items || [];
        if (!cancelled) setInboxCount(items.length);
      } catch (e) {
        console.error('Failed to load inbox count', e);
      }
    }

    loadInboxCount();
    return () => { cancelled = true; };
  }, [groups, selectedCompanyId, claims?.CompanyId, pathname, loadingCompany, setInboxCount]);

  useEffect(() => {
    if (!claims?.CompanyId) {
      setCompanyName(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await client.graphql({
          query: GetCompanyName,
          variables: { id: claims.CompanyId },
          authMode: 'userPool',
        });
        if (!cancelled) setCompanyName(res?.data?.getCompany?.name || null);
      } catch {
        if (!cancelled) setCompanyName(null);
      }
    })();

    return () => { cancelled = true; };
  }, [claims?.CompanyId]);

  // keep your “handover page hides nav”
  const isHandoverPage = pathname.startsWith('/console/handover/');
  if (isHandoverPage) return null;

  const can = (roles) => roles.some((r) => groups.includes(r));
  const is = (p) => pathname.startsWith(p);

  const NavLink = ({ to, active, icon, label, badge, onClick }) => (
    <Link
      to={to}
      onClick={onClick}
      className={`NavItem ${active ? 'NavItemActive' : ''}`}
    >
      <span className="NavIcon" style={{ display: 'inline-flex' }}>
        {icon}
      </span>
      <span>{label}</span>
      {badge > 0 && <span className="NavBadge">{badge}</span>}
    </Link>
  );

  const DesktopSidebar = () => (
    <div className="NavWrap">
      <NavStyles />

      <div className="NavCard">
        <div className="NavInner">
          <Link to="/console/calendar" className="NavBrand">
            <img src={logo} alt="Haste" style={{ height: 34 }} />
          </Link>

          {can(['admin']) && (
            <div className="NavSection">
              <CountryTogglePill />
            </div>
          )}

          <div className="NavLinks">
            {can(['admin', 'country_admin']) && (
              <NavLink
                to="/console/inbox"
                active={is('/console/inbox')}
                icon={<Inbox size={18} />}
                label="Inbox"
                badge={inboxCount}
              />
            )}

            {can(['admin', 'country_admin', 'editor', 'staff']) && (
              <NavLink
                to="/console/calendar"
                active={is('/console/calendar')}
                icon={<Calendar size={18} />}
                label="Calendar"
                badge={0}
              />
            )}

            {can(['admin', 'country_admin', 'editor']) && (
              <NavLink
                to="/console/cars"
                active={is('/console/cars')}
                icon={<Car size={18} />}
                label="Cars"
                badge={0}
              />
            )}

            {can(['admin', 'country_admin', 'editor', 'staff']) && (
              <NavLink
                to="/console/maintenance"
                active={is('/console/maintenance')}
                icon={<Wrench size={18} />}
                label="Maintenance"
                badge={0}
              />
            )}

            {can(['admin']) && (
              <NavLink
                to="/console/prices"
                active={is('/console/prices')}
                icon={<DollarSign size={18} />}
                label="Pricing"
                badge={0}
              />
            )}

            {can(['admin', 'country_admin', 'editor', 'staff']) && (
              <NavLink
                to="/console/accounting"
                active={is('/console/accounting')}
                icon={<ReceiptText size={18} />}
                label="Accounting"
                badge={0}
              />
            )}

            {can(['admin', 'country_admin']) && (
              <NavLink
                to="/console/payments"
                active={is('/console/payments')}
                icon={<CreditCard size={18} />}
                label="Payments"
                badge={0}
              />
            )}

            {can(['admin']) && (
              <NavLink
                to="/console/analytics"
                active={is('/console/analytics')}
                icon={<BarChart3 size={18} />}
                label="Analytics"
                badge={0}
              />
            )}

            {can(['admin']) && (
              <NavLink
                to="/console/users"
                active={is('/console/users')}
                icon={<Users size={18} />}
                label="Users"
                badge={0}
              />
            )}

            {can(['admin']) && (
              <NavLink
                to="/console/approvals"
                active={is('/console/approvals')}
                icon={<CheckCircle2 size={18} />}
                label="Approvals"
                badge={approvalCount}
              />
            )}

            {can(['admin']) && (
              <NavLink
                to="/console/audit"
                active={is('/console/audit')}
                icon={<History size={18} />}
                label="Audit"
                badge={0}
              />
            )}
          </div>

          {claims?.email && (
            <div className="NavUser">
              <div className="NavEmail">{claims.email}</div>
              {companyName && <div className="NavCompany">{companyName}</div>}

              <button onClick={onRequestSignOut} className="NavSignOut">
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const MobileTopbar = () => (
    <nav className="TopBar">
      <NavStyles />

      {/* overlay */}
      <div
        className={`DrawerOverlay ${menuOpen ? 'DrawerOverlayOpen' : ''}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* drawer */}
      <div
        id="mobile-nav-drawer"
        className={`Drawer ${menuOpen ? 'DrawerOpen' : ''}`}
      >
        <div className="DrawerCard">
          <div className="DrawerHeader">
            <Link to="/console/calendar" className="NavBrand" style={{ padding: 8 }}>
              <img src={logo} alt="Haste" style={{ height: 30 }} />
              <div className="NavBrandTitle">
                <div className="t1">Haste</div>
                <div className="t2">Console</div>
              </div>
            </Link>

            <button className="DrawerClose" onClick={() => setMenuOpen(false)}>
              Close
            </button>
          </div>

          <div style={{ padding: 12 }}>
            {can(['admin']) && (
              <div style={{ marginBottom: 10 }}>
                <CountryTogglePill />
              </div>
            )}

            <div className="NavLinks">
              {can(['admin', 'country_admin']) && (
                <NavLink
                  to="/console/inbox"
                  active={is('/console/inbox')}
                  icon={<Inbox size={18} />}
                  label="Inbox"
                  badge={inboxCount}
                  onClick={() => setMenuOpen(false)}
                />
              )}

              {can(['admin', 'country_admin', 'editor', 'staff']) && (
                <NavLink
                  to="/console/calendar"
                  active={is('/console/calendar')}
                  icon={<Calendar size={18} />}
                  label="Calendar"
                  badge={0}
                  onClick={() => setMenuOpen(false)}
                />
              )}

              {can(['admin', 'country_admin', 'editor']) && (
                <NavLink
                  to="/console/cars"
                  active={is('/console/cars')}
                  icon={<Car size={18} />}
                  label="Cars"
                  badge={0}
                  onClick={() => setMenuOpen(false)}
                />
              )}

              {can(['admin', 'country_admin', 'editor', 'staff']) && (
                <NavLink
                  to="/console/maintenance"
                  active={is('/console/maintenance')}
                  icon={<Wrench size={18} />}
                  label="Maintenance"
                  badge={0}
                  onClick={() => setMenuOpen(false)}
                />
              )}

              {can(['admin']) && (
                <NavLink
                  to="/console/prices"
                  active={is('/console/prices')}
                  icon={<DollarSign size={18} />}
                  label="Pricing"
                  badge={0}
                  onClick={() => setMenuOpen(false)}
                />
              )}

              {can(['admin', 'country_admin', 'editor', 'staff']) && (
                <NavLink
                  to="/console/accounting"
                  active={is('/console/accounting')}
                  icon={<ReceiptText size={18} />}
                  label="Accounting"
                  badge={0}
                  onClick={() => setMenuOpen(false)}
                />
              )}

              {can(['admin', 'country_admin']) && (
                <NavLink
                  to="/console/payments"
                  active={is('/console/payments')}
                  icon={<CreditCard size={18} />}
                  label="Payments"
                  badge={0}
                  onClick={() => setMenuOpen(false)}
                />
              )}

              {can(['admin']) && (
                <NavLink
                  to="/console/analytics"
                  active={is('/console/analytics')}
                  icon={<BarChart3 size={18} />}
                  label="Analytics"
                  badge={0}
                  onClick={() => setMenuOpen(false)}
                />
              )}

              {can(['admin']) && (
                <NavLink
                  to="/console/users"
                  active={is('/console/users')}
                  icon={<Users size={18} />}
                  label="Users"
                  badge={0}
                  onClick={() => setMenuOpen(false)}
                />
              )}

              {can(['admin']) && (
                <NavLink
                  to="/console/approvals"
                  active={is('/console/approvals')}
                  icon={<CheckCircle2 size={18} />}
                  label="Approvals"
                  badge={approvalCount}
                  onClick={() => setMenuOpen(false)}
                />
              )}

              {can(['admin']) && (
                <NavLink
                  to="/console/audit"
                  active={is('/console/audit')}
                  icon={<History size={18} />}
                  label="Audit"
                  badge={0}
                  onClick={() => setMenuOpen(false)}
                />
              )}
            </div>

            {claims?.email && (
              <div className="NavUser" style={{ marginTop: 14 }}>
                <div className="NavEmail">{claims.email}</div>
                {companyName && <div className="NavCompany">{companyName}</div>}

                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onRequestSignOut();
                  }}
                  className="NavSignOut"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="TopRow">
        <Link to="/console/calendar" style={{ display: 'flex', alignItems: 'center' }}>
          <img src={logo} alt="Haste" style={{ height: 32 }} />
        </Link>

        {can(['admin']) && <CountryTogglePill />}

        <button
          className="Burger"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen ? 'true' : 'false'}
          aria-controls="mobile-nav-drawer"
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>
    </nav>
  );

  // desktop sidebar content only (Shell places it left)
  if (!isMobile) return <DesktopSidebar />;

  // mobile top bar + drawer
  return <MobileTopbar />;
}


function Shell({ children, sidebar, isMobile }) {
  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: '#f9fafb',
      }}
    >
      {/* Left sidebar (desktop/tablet) */}
      {!isMobile && (
        <aside
          style={{
            width: 260,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 260,
              background: '#ffffff',
              borderRight: '1px solid #e5e7eb',
              position: 'fixed',
              top: 0,
              left: 0,
              height: '100vh',
              overflowY: 'auto',
              zIndex: 40,
            }}
          >
            {sidebar}
          </div>
        </aside>
      )}

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <main
          style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : 1200,
            margin: '0 auto',
            padding: isMobile ? '12px 10px 22px' : '16px 12px',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}


function NotFound() {
  return (
    <div style={{ padding: 24 }}>
      <h3 style={{ marginTop: 0 }}>404 — Not Found</h3>
      <p>The page you’re looking for doesn’t exist.</p>
      <Link to="/console/calendar">Go to Calendar</Link>
    </div>
  );
}

/* -------------------- Console app wrapper under /console/* -------------------- */
function ConsoleApp() {
  const [inboxCount, setInboxCount] = useState(0);
  const isMobile = useIsMobile();
  const [confirmSignOutOpen, setConfirmSignOutOpen] = useState(false);
  const [doSignOut, setDoSignOut] = useState(null);
  return (
    <ConsoleAuthGate
      components={{
        Header() {
          return (
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <img src={logo} alt="Haste" style={{ height: 48, marginBottom: 8 }} />
              <div style={{ fontSize: 14, color: '#555' }}>Car Rental Control Panel</div>
            </div>
          );
        },
        Footer() {
          return (
            <div
              style={{
                fontSize: 11,
                color: '#888',
                textAlign: 'center',
                marginTop: 8,
              }}
            >
              Powered by HASTE • Internal use only
            </div>
          );
        },
      }}
      formFields={{
        signIn: {
          username: {
            label: 'Email or username',
            placeholder: 'you@example.com or username',
          },
        },
      }}
    >
      {({ signOut }) => (
        <InboxBadgeContext.Provider value={{ inboxCount, setInboxCount }}>
          <CountryProvider>
            {/* Mobile topbar */}
            {isMobile && (
              <Nav
                onRequestSignOut={() => {
                  setDoSignOut(() => signOut);
                  setConfirmSignOutOpen(true);
                }}
              />
            )}

            <Shell
              isMobile={isMobile}
              sidebar={
                !isMobile ? (
                  <Nav
                    onRequestSignOut={() => {
                      setDoSignOut(() => signOut);
                      setConfirmSignOutOpen(true);
                    }}
                  />
                ) : null
              }
            >



              <Routes>
                {/* /console → /console/calendar */}
                <Route index element={<Navigate to="calendar" replace />} />

                <Route
                  path="inbox"
                  element={
                    <RequireGroup allowed={['admin', 'country_admin']}>
                      <InboxPage />
                    </RequireGroup>
                  }
                />

                <Route
                  path="calendar"
                  element={
                    <RequireGroup allowed={['admin', 'country_admin', 'editor', 'staff']}>
                      <CalendarPage />
                    </RequireGroup>
                  }
                />
                <Route
                  path="handover/:reservationId/:type"
                  element={
                    <RequireGroup
                      allowed={['admin', 'country_admin', 'editor', 'staff']}
                    >
                      <HandoverFormPage />
                    </RequireGroup>
                  }
                />

                <Route
                  path="cars"
                  element={
                    <RequireGroup allowed={['admin', 'country_admin', 'editor']}>
                      <CarsPage />
                    </RequireGroup>
                  }
                />

                <Route
                  path="maintenance"
                  element={
                    <RequireGroup allowed={['admin', 'country_admin', 'editor', 'staff']}>
                      <MaintenancePage />
                    </RequireGroup>
                  }
                />

                <Route
                  path="prices"
                  element={
                    <RequireGroup allowed={['admin']}>
                      <PricesPage />
                    </RequireGroup>
                  }
                />

                <Route
                  path="accounting"
                  element={
                    <RequireGroup allowed={['admin', 'country_admin', 'editor', 'staff']}>
                      <AccountingPage />
                    </RequireGroup>
                  }
                />

                <Route
                  path="payments"
                  element={
                    <RequireGroup allowed={['admin', 'country_admin']}>
                      <PaymentOpsPage />
                    </RequireGroup>
                  }
                />

                <Route
                  path="analytics"
                  element={
                    <RequireGroup allowed={['admin']}>
                      <AnalyticsPage />
                    </RequireGroup>
                  }
                />

                <Route
                  path="users"
                  element={
                    <RequireGroup allowed={['admin']}>
                      <UsersPage />
                    </RequireGroup>
                  }
                />

                <Route
                  path="approvals"
                  element={
                    <RequireGroup allowed={['admin']}>
                      <ApprovalsPage />
                    </RequireGroup>
                  }
                />

                <Route
                  path="audit"
                  element={
                    <RequireGroup allowed={['admin']}>
                      <AuditTrailPage />
                    </RequireGroup>
                  }
                />

                <Route path="/handover-media" element={<HandoverMediaPage />} />
              </Routes>
            </Shell>
            <ConfirmModal
              open={confirmSignOutOpen}
              title="Sign out"
              message="Are you sure you want to sign out from Haste Console?"
              onCancel={() => setConfirmSignOutOpen(false)}
              onConfirm={() => {
                setConfirmSignOutOpen(false);
                doSignOut?.();
              }}
            />
          </CountryProvider>
        </InboxBadgeContext.Provider>
      )}
    </ConsoleAuthGate>
  );
}

/* -------------------- Root App -------------------- */
export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/console/*" element={<ConsoleApp />} />
        <Route path="/payment/return" element={<PayReturnPage />} />
        <Route path="/payment/cancel" element={<PayCancelPage />} />
        <Route path="/payment/start" element={<PayStartPage />} />
        <Route path="/handover-media" element={<HandoverMediaPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
